<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
         Upload your consolidated statements and any related trust documents and articles of incorporation. You can select multiple files.
        </p>
      </div>
    </help-drawer>
    <v-form name="PershingConsolidatedStatement" class="form" data-test-id="pershing-consolidated-statement-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.PERSHING_CONSOLIDATED_STATEMENT.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1>
              <span class="headerlight">
                Please upload your <strong>Consolidated Statement, Trust Documents and Articles of Incorporation (if applicable) </strong>(multiple files supported)
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>

        <div v-if="isSyncing" class="syncing-message">
          <p>Uploading your files...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isComplete && formHidden && !isSyncing"
          :submitted="isComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />

        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container large">
              <div class="file-upload-container">
                <div
                  v-if="!selectedFiles || !selectedFiles.length"
                  class="placeholder-add"
                  role="button"
                  tabindex="0"
                  aria-label="Add a file"
                  @click="triggerAddFiles"
                  @keydown.enter.prevent="triggerAddFiles"
                  @keydown.space.prevent="triggerAddFiles"
                >
                  <v-icon large class="mr-2">mdi-file-plus</v-icon>
                  <span class="placeholder-text">Add a file</span>
                </div>
                <ul class="file-list" v-if="selectedFiles && selectedFiles.length">
                  <li v-for="(file, index) in selectedFiles" :key="file.name + '-' + index" class="file-list-item">
                    <div class="file-meta">
                      <v-icon small class="mr-2">mdi-file</v-icon>
                      <span class="name">{{ file.name }}</span>
                      <span class="size grey--text text--darken-1">({{ formatBytes(file.size) }})</span>
                    </div>
                    <v-btn text small color="error" @click="removeFile(index)" aria-label="Remove file">
                      <v-icon left small>mdi-delete</v-icon>
                      Remove
                    </v-btn>
                  </li>
                </ul>
                <div class="error-messages" v-if="$v.selectedFiles.$dirty && fileErrors.length">
                  <div class="error red--text text--darken-2" v-for="(err, i) in fileErrors" :key="'err-' + i">
                    {{ err }}
                  </div>
                </div>
                <v-btn
                  v-if="selectedFiles && selectedFiles.length"
                  color="primary"
                  text
                  small
                  class="mt-2"
                  @click="triggerAddFiles"
                >
                  <v-icon left small>mdi-plus</v-icon>
                  Add another file
                </v-btn>
                <input
                  ref="hiddenFileInput"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  class="hidden-file-input"
                  @change="onFilesSelected"
                />
                <div class="file-info mt-2">
                  <p class="caption">Accepted: PDF, JPG, JPEG, PNG</p>
                  <p class="caption">Max size per file: 10MB</p>
                </div>
              </div>
            </v-col>
          </v-row>

          <div class="skip-for-now">
            <a @click="skip()">Continue without uploading</a>
          </div>
        </div>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()"></bottom-nav>
  </div>
  
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required } from 'vuelidate/lib/validators';
import { MENUS } from '@/common/constants';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';
import ApiService from '@/services/api.service';
import { NotificationService, NOTIFICATION_TYPES } from '@/services/notification.service';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default {
  name: 'PershingConsolidatedStatement',
  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],
  data: () => ({
    selectedFiles: [],
    MENUS: MENUS,
  }),
  validations() {
    return {
      selectedFiles: { required },
      validationGroup: ['selectedFiles'],
    };
  },
  computed: {
    isComplete() {
      return this.isPageComplete(MENUS.CLIENT.PERSHING_CONSOLIDATED_STATEMENT.id);
    },
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.PERSHING_CONSOLIDATED_STATEMENT.id);
      return page && page.status === 'IN_PROGRESS';
    },
    helpTitle() {
      return 'Why do you need this?';
    },
    interviewId() {
      return this.$store.state.auth.sessionToken;
    },
    fileErrors() {
      const errors = [];
      if (!this.$v.selectedFiles.$dirty) return errors;
      if (!this.$v.selectedFiles.required) {
        errors.push('Please select at least one file');
      }
      if (this.selectedFiles && this.selectedFiles.some(f => f.size >= MAX_FILE_SIZE)) {
        errors.push('Each file must be < 10 MB!');
      }
      return errors;
    },
  },
  methods: {
    triggerAddFiles() {
      const input = this.$refs.hiddenFileInput;
      if (input) input.click();
    },
    onFilesSelected(event) {
      const input = event.target;
      const files = Array.from(input.files || []);
      if (files.length) {
        this.selectedFiles = [...this.selectedFiles, ...files];
        this.$v && this.$v.selectedFiles && this.$v.selectedFiles.$touch();
      }
      // reset input value so the same file can be selected again
      if (this.$refs.hiddenFileInput) this.$refs.hiddenFileInput.value = null;
    },
    removeFile(index) {
      if (!this.selectedFiles || index < 0 || index >= this.selectedFiles.length) return;
      const updated = [...this.selectedFiles];
      updated.splice(index, 1);
      this.selectedFiles = updated;
      this.$v && this.$v.selectedFiles && this.$v.selectedFiles.$touch();
    },
    formatBytes(bytes) {
      if (bytes === 0 || bytes === undefined || bytes === null) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      const value = (bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1);
      return `${value} ${sizes[i]}`;
    },
    async uploadStatements() {
      if (!this.selectedFiles || this.selectedFiles.length === 0) return;
      const formData = new FormData();
      this.selectedFiles.forEach((f) => formData.append('statements', f));
      const url = `/interviews/${this.interviewId}/pershing/consolidated-statements`;
      return ApiService.patch(url, formData);
    },
    async next(route = null) {
      let to = route || this.pages[this.pageIndex + 1];
      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }
      this.markPageAsSyncing(this.pages[this.pageIndex]);
      try {
        await this.uploadStatements();
        this.formHidden = true;
        await this.$store.dispatch('GET_INTERVIEW_PROGRESS');
        this.goNext(to);
      } catch (error) {
        const page = this.getPage(this.pages[this.pageIndex]);
        if (page) page.status = null;
        this.$v.$reset();
        NotificationService.alert({
          type: NOTIFICATION_TYPES.ERROR,
          title: 'Upload Failed',
          message: 'Failed to upload files. Please try again.',
          okTitle: 'Ok',
        });
      }
    },
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },
    skip() {
      const data = { skipped: true };
      this.markPageAsSyncing(this.pages[this.pageIndex]);
      this.completeStep(this.pages[this.pageIndex], data)
        .then(() => this.goNext(this.pages[this.pageIndex + 1]))
        .catch(() => {});
    },
    toggleFormState(hide) {
      this.formHidden = hide;
      if (!hide) {
        this.selectedFiles = [];
      }
      this.$nextTick(() => this.$v && this.$v.$reset());
    },
  },
  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.PERSHING_CONSOLIDATED_STATEMENT.id);
    const page = this.getPage(MENUS.CLIENT.PERSHING_CONSOLIDATED_STATEMENT.id);
    this.formHidden = page && page.lastCompleted !== null && page.status === 'SYNCED';
  },
  mounted() {
    this.$nextTick(() => {
      const page = this.getPage(MENUS.CLIENT.PERSHING_CONSOLIDATED_STATEMENT.id);
      if (page && page.lastCompleted !== null && page.status === 'SYNCED') {
        this.formHidden = true;
      }
    });
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  },
};
</script>

<style lang="scss" scoped>
.file-upload-container {
  margin: 0 auto;
  max-width: 500px;
}
.file-info { text-align: center; }
.syncing-message { text-align: center; margin: 20px 0; }
ul.file-list { list-style: none; padding: 0; margin: 0 0 8px; }
.file-list-item { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; border: 1px solid #eee; border-radius: 6px; margin-bottom: 8px; }
.file-meta { display: flex; align-items: center; }
.file-meta .name { margin-right: 6px; }
.hidden-file-input { display: none; }
.error-messages { margin-top: 4px; }
.placeholder-add { display: flex; align-items: center; justify-content: center; padding: 16px; border: 2px dashed #c7c7c7; border-radius: 8px; color: #666; cursor: pointer; margin-bottom: 12px; }
.placeholder-add:hover { background: #fafafa; }
.placeholder-text { font-weight: 500; }
</style>


